import React, { useState } from 'react';
import { ProcessStep, ProcessStepRisk, ProcessStepControl } from '../../services/ProcessStepService';
import { ChevronDown, ChevronUp, Edit, Plus, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { RiskItem } from './RiskItem';
import { ControlItem } from './ControlItem';
import { AddRiskModal } from './AddRiskModal';
import { AddControlModal } from './AddControlModal';
import { EditStepModal } from './EditStepModal';

interface ExtendedProcessStep extends ProcessStep {
  risks?: ProcessStepRisk[];
  controls?: ProcessStepControl[];
  inherent_risk_level?: 'low' | 'medium' | 'high';
  residual_risk_level?: 'low' | 'medium' | 'high';
  control_effectiveness_percentage?: number;
}

interface ConfigurationData {
  countries: Array<{ id: string; name: string }>;
  legalEntities: Array<{ id: string; name: string }>;
  supportingModels: Array<{ id: string; name: string }>;
  thirdPartyBusinessTypes: Array<{ id: string; name: string }>;
}

interface ProcessStepItemProps {
  step: ExtendedProcessStep;
  isExpanded: boolean;
  onToggleExpansion: (stepId: number) => void;
  onUpdateStep: (stepId: number, updateData: Partial<ProcessStep>) => Promise<void>;
  onAddRisk: (stepId: number, riskData: Partial<ProcessStepRisk>) => Promise<void>;
  onAddControl: (stepId: number, controlData: Partial<ProcessStepControl>) => Promise<void>;
  configurations: ConfigurationData;
}

export const ProcessStepItem: React.FC<ProcessStepItemProps> = ({
  step,
  isExpanded,
  onToggleExpansion,
  onUpdateStep,
  onAddRisk,
  onAddControl,
  configurations
}) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddRiskModal, setShowAddRiskModal] = useState(false);
  const [showAddControlModal, setShowAddControlModal] = useState(false);

  const getRiskBadgeColor = (level: 'low' | 'medium' | 'high' | undefined) => {
    switch (level) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getControlEffectivenessStatus = (percentage: number) => {
    if (percentage >= 90) return { text: 'Effective', color: 'bg-green-100 text-green-800' };
    if (percentage >= 70) return { text: 'Needs Improvement', color: 'bg-yellow-100 text-yellow-800' };
    return { text: 'Ineffective', color: 'bg-red-100 text-red-800' };
  };

  const effectivenessStatus = getControlEffectivenessStatus(step.control_effectiveness_percentage || 0);

  return (
    <>
      <div className="bg-white border-b border-gray-200 hover:bg-gray-50 transition-colors">
        {/* Step Header */}
        <div 
          className="p-6 cursor-pointer flex items-center justify-between"
          onClick={() => onToggleExpansion(step.id)}
        >
          <div className="flex-1 flex items-center">
            <div className="flex items-center mr-4">
              <div className="w-8 h-8 bg-[#57C8F4] text-white rounded-full flex items-center justify-center text-sm font-semibold mr-4">
                {step.step_number}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#221551] mb-1">
                  {step.process_step_name}
                </h3>
                <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                  {step.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-[#30A8CF] text-white text-xs rounded-full">
                    Operational Risk
                  </span>
                  <span className="px-2 py-1 bg-[#57C8F4] text-white text-xs rounded-full">
                    {step.process_step_type || 'Manual'}
                  </span>
                  <span className="px-2 py-1 bg-[#454F83] text-white text-xs rounded-full">
                    {step.controls?.length || 0} Controls
                  </span>
                </div>
                <div className="text-xs text-[#454F83] italic mt-1">
                  Process Step ID: {step.process_step_id || `STEP-${step.id}`}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Risk Level */}
            <div className="flex flex-col items-end gap-1">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRiskBadgeColor(step.inherent_risk_level)}`}>
                {step.inherent_risk_level?.charAt(0).toUpperCase() + (step.inherent_risk_level?.slice(1) || '')} Risk
              </span>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${effectivenessStatus.color}`}>
                {effectivenessStatus.text}
              </span>
            </div>

            {/* Control Effectiveness */}
            <div className="text-right text-sm">
              <div className="font-medium text-[#221551]">
                {step.controls?.filter(c => c.control_effectiveness === 'Effective').length || 0}/
                {step.controls?.length || 0} Controls Effective
              </div>
              <div className="text-[#454F83]">
                {step.control_effectiveness_percentage || 0}% Effective
              </div>
            </div>

            {/* Expand/Collapse Icon */}
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-[#454F83]" />
            ) : (
              <ChevronDown className="h-5 w-5 text-[#454F83]" />
            )}
          </div>
        </div>

        {/* Expanded Details */}
        {isExpanded && (
          <div className="bg-gray-50 p-6 border-t border-gray-200">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Step Metadata */}
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h4 className="font-semibold text-[#221551] text-sm uppercase tracking-wide mb-3">
                  Step Details
                </h4>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="font-medium text-[#454F83]">Type:</span>
                    <span className="ml-2 text-[#221551]">{step.process_step_type || 'Manual'}</span>
                  </div>
                  {step.performing_country && (
                    <div>
                      <span className="font-medium text-[#454F83]">Performing Country:</span>
                      <span className="ml-2 text-[#221551]">
                        {Array.isArray(step.performing_country) 
                          ? step.performing_country.join(', ')
                          : step.performing_country}
                      </span>
                    </div>
                  )}
                  {step.third_party_support_type && (
                    <div>
                      <span className="font-medium text-[#454F83]">Third Party Support:</span>
                      <span className="ml-2 text-[#221551]">{step.third_party_support_type}</span>
                    </div>
                  )}
                  <div className="pt-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowEditModal(true);
                      }}
                      className="flex items-center gap-2 text-[#30A8CF] hover:text-[#57C8F4] transition-colors"
                    >
                      <Edit className="h-4 w-4" />
                      Edit Details
                    </button>
                  </div>
                </div>
              </div>

              {/* Associated Risks */}
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-[#221551] text-sm uppercase tracking-wide">
                    Associated Risks
                  </h4>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAddRiskModal(true);
                    }}
                    className="text-[#30A8CF] hover:text-[#57C8F4] transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {step.risks?.map((risk) => (
                    <RiskItem key={risk.id} risk={risk} />
                  )) || (
                    <div className="text-gray-500 text-sm italic">No risks identified</div>
                  )}
                </div>
              </div>

              {/* Key Controls */}
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-[#221551] text-sm uppercase tracking-wide">
                    Key Controls
                  </h4>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAddControlModal(true);
                    }}
                    className="text-[#30A8CF] hover:text-[#57C8F4] transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {step.controls?.map((control) => (
                    <ControlItem key={control.id} control={control} />
                  )) || (
                    <div className="text-gray-500 text-sm italic">No controls identified</div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6 justify-end">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowEditModal(true);
                }}
                className="px-4 py-2 bg-[#57C8F4] text-white rounded-md hover:bg-[#30A8CF] transition-colors"
              >
                Edit Step
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAddRiskModal(true);
                }}
                className="px-4 py-2 border border-[#30A8CF] text-[#30A8CF] rounded-md hover:bg-[#30A8CF] hover:text-white transition-colors"
              >
                Add Risk
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAddControlModal(true);
                }}
                className="px-4 py-2 border border-[#30A8CF] text-[#30A8CF] rounded-md hover:bg-[#30A8CF] hover:text-white transition-colors"
              >
                Add Control
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // View audit trail functionality would go here
                }}
                className="px-4 py-2 border border-[#30A8CF] text-[#30A8CF] rounded-md hover:bg-[#30A8CF] hover:text-white transition-colors"
              >
                View Audit Trail
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showEditModal && (
        <EditStepModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          step={step}
          onSave={(updateData) => onUpdateStep(step.id, updateData)}
          configurations={configurations}
        />
      )}

      {showAddRiskModal && (
        <AddRiskModal
          isOpen={showAddRiskModal}
          onClose={() => setShowAddRiskModal(false)}
          onSave={(riskData) => onAddRisk(step.id, riskData)}
        />
      )}

      {showAddControlModal && (
        <AddControlModal
          isOpen={showAddControlModal}
          onClose={() => setShowAddControlModal(false)}
          onSave={(controlData) => onAddControl(step.id, controlData)}
        />
      )}
    </>
  );
};